import React, { useMemo, useState } from 'react';
import {
  Collapse, Checkbox, Button, Space, Typography, Input, Switch, Tag, Tooltip, Empty,
} from 'antd';
import {
  buildGroupedModules, actionLabel, fieldLabel, MODULE_META,
} from '../constants/moduleGroups';

const { Text, Paragraph } = Typography;

// Reusable permission editor driven by the backend catalog
// ({ modules:[], catalog:{ module: { actions:[], fields:[] } } }).
//
// Value shape (what the API stores) is unchanged:
//   [{ module, topLevel:[], viewableFields:[], editableFields:[],
//      deny:{ topLevel:[], viewableFields:[], editableFields:[] } }]
//
// For every action/field you can independently GRANT and DENY. Deny wins at
// resolution time and lets a high-precedence set subtract access granted earlier.
// 'all' is offered as a wildcard on every list.
//
// The editor lists EVERY catalog module, grouped the way the sidebar is grouped
// (see constants/moduleGroups.js), instead of hiding them behind an "add module"
// search over raw keys — that made per-screen modules like `leadManagement`
// ("Manage Leads") effectively unfindable. A module lands in the saved value as
// soon as it has one grant or deny, and drops out again when it has none, so
// showing everything costs nothing in the payload.

const WILDCARD = 'all';

const emptyDeny = () => ({ topLevel: [], viewableFields: [], editableFields: [] });

const ensurePerm = (p) => ({
  module: p.module,
  topLevel: p.topLevel || [],
  viewableFields: p.viewableFields || [],
  editableFields: p.editableFields || [],
  deny: { ...emptyDeny(), ...(p.deny || {}) },
});

const isEmptyPerm = (p) =>
  !p.topLevel.length && !p.viewableFields.length && !p.editableFields.length
  && !p.deny.topLevel.length && !p.deny.viewableFields.length && !p.deny.editableFields.length;

const grantCountOf = (p) => p.topLevel.length + p.viewableFields.length + p.editableFields.length;
const denyCountOf = (p) => p.deny.topLevel.length + p.deny.viewableFields.length + p.deny.editableFields.length;

const withWildcard = (arr = []) => [WILDCARD, ...arr];
const actionOptions = (arr) => withWildcard(arr).map((a) => ({ label: actionLabel(a), value: a }));
const fieldOptions = (arr) => withWildcard(arr).map((f) => ({ label: fieldLabel(f), value: f }));

const PermissionTreeEditor = ({ catalog, modules = [], value = [], onChange }) => {
  const [search, setSearch] = useState('');
  // Deny is the advanced half of the model; only surface it when it is in use
  // or explicitly asked for, so the common "tick view" case stays readable.
  const [openGroups, setOpenGroups] = useState([]);
  const [showDeny, setShowDeny] = useState(
    () => (value || []).some((p) => denyCountOf(ensurePerm(p)) > 0),
  );

  const perms = useMemo(() => (value || []).map(ensurePerm), [value]);
  const permByModule = useMemo(
    () => perms.reduce((acc, p) => { acc[p.module] = p; return acc; }, {}),
    [perms],
  );

  const groups = useMemo(() => buildGroupedModules(modules), [modules]);

  const term = search.trim().toLowerCase();

  // Search matches the friendly label, the hint and the group name as well as
  // the raw key — searching "leads" has to find `leadManagement`.
  const visibleGroups = useMemo(() => {
    if (!term) return groups;
    const matches = (m) => {
      const meta = MODULE_META[m.key] || {};
      return [m.key, m.label, meta.hint, meta.groupLabel]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(term));
    };
    return groups
      .map((g) => ({ ...g, modules: g.modules.filter(matches) }))
      .filter((g) => g.modules.length);
  }, [groups, term]);

  const emit = (next) => onChange && onChange(next.filter((p) => !isEmptyPerm(p)));

  // Replace (or insert) one module's entry, preserving the existing order.
  const upsert = (module, updater) => {
    const existing = permByModule[module];
    const base = existing || ensurePerm({ module });
    const updated = updater(base);
    if (existing) return emit(perms.map((p) => (p.module === module ? updated : p)));
    return emit([...perms, updated]);
  };

  // path: 'grant' | 'deny'; bucket: 'topLevel' | 'viewableFields' | 'editableFields'
  const setList = (module, path, bucket, list) =>
    upsert(module, (p) => (path === 'grant'
      ? { ...p, [bucket]: list }
      : { ...p, deny: { ...p.deny, [bucket]: list } }));

  const clearModule = (module) => emit(perms.filter((p) => p.module !== module));

  const applyToModules = (moduleKeys, mode) => {
    const keys = new Set(moduleKeys);
    if (mode === 'clear') return emit(perms.filter((p) => !keys.has(p.module)));

    const next = [...perms];
    moduleKeys.forEach((module) => {
      const idx = next.findIndex((p) => p.module === module);
      const base = idx >= 0 ? next[idx] : ensurePerm({ module });
      const updated = mode === 'full'
        ? { ...base, topLevel: [WILDCARD], viewableFields: [WILDCARD], editableFields: [WILDCARD] }
        : { ...base, topLevel: Array.from(new Set([...base.topLevel, 'view'])) };
      if (idx >= 0) next[idx] = updated; else next.push(updated);
    });
    return emit(next);
  };

  const renderBucket = (perm, label, bucket, options) => {
    if (!options.length) return null;
    return (
      <div style={{ marginBottom: 10 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{label}</Text>
        <Checkbox.Group
          style={{ display: 'block' }}
          options={options}
          value={perm[bucket]}
          onChange={(vals) => setList(perm.module, 'grant', bucket, vals)}
        />
        {showDeny && (
          <div style={{ marginTop: 4 }}>
            <Text type="danger" style={{ fontSize: 12, display: 'block' }}>Deny {label.toLowerCase()}</Text>
            <Checkbox.Group
              style={{ display: 'block' }}
              options={options}
              value={perm.deny[bucket]}
              onChange={(vals) => setList(perm.module, 'deny', bucket, vals)}
            />
          </div>
        )}
      </div>
    );
  };

  const renderModule = (mod) => {
    const def = (catalog && catalog[mod.key]) || { actions: [], fields: [] };
    const perm = permByModule[mod.key] || ensurePerm({ module: mod.key });
    const grants = grantCountOf(perm);
    const denies = denyCountOf(perm);
    const meta = MODULE_META[mod.key] || {};

    return (
      <div
        key={mod.key}
        style={{
          border: '1px solid #f0f0f0',
          borderLeft: `3px solid ${grants ? '#1677ff' : '#f0f0f0'}`,
          borderRadius: 6,
          padding: '10px 12px',
          marginBottom: 8,
          background: grants || denies ? '#fafcff' : '#fff',
        }}
      >
        <Space size={8} wrap style={{ marginBottom: 2 }}>
          <Text strong>{mod.label}</Text>
          <Tooltip title="Permission key used in code">
            <Text code style={{ fontSize: 11 }}>{mod.key}</Text>
          </Tooltip>
          {grants > 0 && <Tag color="blue">{grants} grant</Tag>}
          {denies > 0 && <Tag color="red">{denies} deny</Tag>}
          {(grants > 0 || denies > 0) && (
            <Button size="small" type="link" danger onClick={() => clearModule(mod.key)}>
              Clear
            </Button>
          )}
        </Space>
        {meta.hint && (
          <Paragraph type="secondary" style={{ fontSize: 12, margin: '0 0 8px' }}>{meta.hint}</Paragraph>
        )}

        {renderBucket(perm, 'Actions', 'topLevel', actionOptions(def.actions))}
        {!!def.fields.length && renderBucket(perm, 'Viewable fields', 'viewableFields', fieldOptions(def.fields))}
        {!!def.fields.length && renderBucket(perm, 'Editable fields', 'editableFields', fieldOptions(def.fields))}
      </div>
    );
  };

  const items = visibleGroups.map((group) => {
    const keys = group.modules.map((m) => m.key);
    const configured = keys.filter((k) => permByModule[k]).length;
    return {
      key: group.key,
      label: (
        <Space size={8}>
          <Text strong>{group.label}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {configured}/{keys.length} configured
          </Text>
        </Space>
      ),
      extra: (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Grant 'view' on every module in this group">
            <Button size="small" onClick={() => applyToModules(keys, 'view')}>View all</Button>
          </Tooltip>
          <Tooltip title="Grant the 'all' wildcard on every module in this group">
            <Button size="small" onClick={() => applyToModules(keys, 'full')}>Full</Button>
          </Tooltip>
          <Button size="small" danger onClick={() => applyToModules(keys, 'clear')}>Clear</Button>
        </Space>
      ),
      children: group.modules.map(renderModule),
    };
  });

  const configuredCount = perms.length;

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input.Search
          allowClear
          placeholder="Search modules (e.g. leads, vault, payroll)"
          style={{ width: 320 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Tag color={configuredCount ? 'blue' : 'default'}>
          {configuredCount} module{configuredCount === 1 ? '' : 's'} configured
        </Tag>
        <Space size={6}>
          <Switch size="small" checked={showDeny} onChange={setShowDeny} />
          <Text type="secondary" style={{ fontSize: 12 }}>Show deny controls</Text>
        </Space>
        {configuredCount > 0 && (
          <Button size="small" danger onClick={() => emit([])}>Clear all</Button>
        )}
      </Space>

      {items.length === 0 ? (
        <Empty description={term ? `No module matches "${search}"` : 'No modules in catalog'} />
      ) : (
        <Collapse
          items={items}
          // While searching, open every matching group so the hit is visible
          // without a second click; otherwise respect what the user opened.
          activeKey={term ? items.map((i) => i.key) : openGroups}
          onChange={(keys) => !term && setOpenGroups(keys)}
        />
      )}
    </div>
  );
};

export default PermissionTreeEditor;
